<template>
    <div class="details-elements-grid">
        <template v-if="event.isOnline && (event.onlineApplication || (isAppointment && event.onlineLink))">
            <EventEntryDetailsElement
                :heading="$t('iFrame.online')"
                icon="globe">
                <EventEntryOnlineInfo
                    :link="event.onlineLink"
                    :application="event.onlineApplication"
                    :appointment="isAppointment" />
            </EventEntryDetailsElement>
        </template>
        <template v-if="event.location && event.location.address">
            <EventEntryDetailsElement
                :heading="$t('iFrame.location')"
                icon="map-marker-alt">
                <div
                    v-if="event.location.name"
                    class="font-weight-bold">
                    {{ event.location.name }}
                </div>
                <div v-if="event.location.room">
                    {{ event.location.room }}
                </div>
                <div
                    v-if="event.location.address.district &&
                        event.location.address.district !== event.location.address.city">
                    {{ event.location.address.district }}
                </div>
                <div style="white-space: pre-line">
                    {{ event.location.address.addressAsString }}
                </div>
                <div v-if="event.location.hompage">
                    <a :href="event.location.hompage">{{ event.location.hompage }}</a>
                </div>
                <div v-if="event.location.lowBarrier && event.location.lowBarrier === 1">
                    <font-awesome-icon
                        icon="wheelchair"
                        class="mr-1" />
                    {{ $t('iFrame.lowBarrier') }}
                </div>
            </EventEntryDetailsElement>
        </template>
        <template v-if="event.moreInformationLink">
            <EventEntryDetailsElement
                :heading="$t('iFrame.moreInfoLink')"
                icon="link">
                <a
                    :href="event.moreInformationLink"
                    target="_blank">{{ event.moreInformationLink }}</a>
            </EventEntryDetailsElement>
        </template>
        <template v-if="event.personInCharge">
            <EventEntryDetailsElement
                :heading="$t('iFrame.processing')"
                icon="address-card">
                <div v-if="event.personInCharge.name">
                    <strong>{{ event.personInCharge.name }}</strong>
                </div>
                <div v-if="event.personInCharge.phone">
                    <strong>{{ $t('iFrame.phone') }}:</strong>
                    <a :href="'tel:' + event.personInCharge.phone">{{ event.personInCharge.phone }}</a>
                </div>
                <div v-if="event.personInCharge.mail">
                    <strong>{{ $t('iFrame.mail') }}:</strong>
                    <a :href="'mailto:' + event.personInCharge.mail">{{ event.personInCharge.mail }}</a>
                </div>
                <div v-if="event.personInCharge.contactHours">
                    <div><strong>{{ $t('iFrame.contactHours') }}:</strong></div>
                    <div style="white-space: pre-line">
                        {{ event.personInCharge.contactHours }}
                    </div>
                </div>
            </EventEntryDetailsElement>
        </template>
        <template v-if="event.occupancy">
            <EventEntryDetailsElement
                :heading="$t('iFrame.occupancy')"
                icon="home">
                <div
                    v-if="event.occupancy === 'free'"
                    class="text-success">
                    {{ $t('iFrame.freePlaces') }}
                </div>
                <div
                    v-else-if="event.occupancy === 'few'"
                    class="text-warning">
                    {{ $t('iFrame.fewPlaces') }}
                </div>
                <div
                    v-else-if="event.occupancy === 'full'"
                    class="text-danger">
                    <span>{{ $t('iFrame.isBookedOut') }}</span>
                    <span v-if="event.allowRegistrationIfFullyBooked">{{ $t('iFrame.registerToWaitingList') }}</span>
                </div>
            </EventEntryDetailsElement>
        </template>
        <template v-if="event.minParticipants || event.maxParticipants">
            <EventEntryDetailsElement
                :heading="$t('iFrame.participants')"
                icon="users">
                <div>{{ minMaxParticipantsSentence }}</div>
            </EventEntryDetailsElement>
        </template>
        <template v-if="!event.registrationDeactivated && event.registrationEnd">
            <EventEntryDetailsElement
                :heading="$t('iFrame.registrationDeadline')"
                icon="hourglass-end">
                <div>{{ $df($moment(event.registrationEnd), 'dateLong') }}</div>
            </EventEntryDetailsElement>
        </template>
        <template v-if="event.additionalDates">
            <EventEntryDetailsElement
                :heading="$t('iFrame.additionalDates')"
                icon="calendar-alt">
                <div style="white-space: pre-line">
                    {{ event.additionalDates }}
                </div>
            </EventEntryDetailsElement>
        </template>
        <template v-if="isDocumentDetailsElementVisible">
            <EventEntryDetailsElement
                :heading="$t('iFrame.documents')"
                icon="file">
                <DocumentListItem
                    v-if="termsDocument"
                    :document="termsDocument"
                    :name="$t('iFrame.terms')" />
                <DocumentListItem
                    v-if="privacyDocument"
                    :document="privacyDocument"
                    :name="$t('iFrame.privacy')" />
                <DocumentListItem
                    v-if="infosheetDocument"
                    :document="infosheetDocument"
                    :name="$t('iFrame.infoSheet')" />
                <DocumentListItem
                    v-for="document in event.documents"
                    :key="'document-' + document.id"
                    :document="document"
                    :name="document.fileName" />
            </EventEntryDetailsElement>
        </template>
        <template v-if="event.offerNumber">
            <EventEntryDetailsElement
                :heading="$t('iFrame.offerNumber')"
                icon="hashtag">
                <div>{{ event.offerNumber }}</div>
            </EventEntryDetailsElement>
        </template>
        <template v-if="event.organizer || event.alternativeOrganizer">
            <EventEntryDetailsElement
                :heading="$t('iFrame.organizer')"
                icon="user-tie">
                <OrganizerTooltip
                    v-if="event.alternativeOrganizer"
                    :organizer="event.alternativeOrganizer" />
                <OrganizerTooltip
                    v-else
                    :organizer="event.organizer" />
            </EventEntryDetailsElement>
        </template>
    </div>
</template>

<script>
import upperFirst from "lodash/upperFirst";
import EventEntryDetailsElement
    from "@/components/pages/eventIFrame/EventEntry/EventEntryDetails/EventEntryDetailsElement";
import DocumentListItem
    from "@/components/pages/eventIFrame/EventEntry/EventEntryDetails/EventEntryInfo/DocumentListItem";
import OrganizerTooltip
    from "@/components/pages/eventIFrame/EventEntry/EventEntryDetails/EventEntryInfo/OrganizerTooltip";
import EventEntryOnlineInfo from '../../EventEntryOverview/EventEntryOnlineInfo'

export default {
    name: "EventEntryMoreDetails",
    components: {EventEntryOnlineInfo, DocumentListItem, EventEntryDetailsElement, OrganizerTooltip},
    inject: ['iFrameType'],
    props: {
        event: {
            type: Object,
            required: true
        }
    },
    computed: {
        isAppointment() {
            return this.iFrameType() === 'appointments'
        },
        minMaxParticipantsSentence() {
            let string = "";
            if (this.event.minParticipants) {
                string += this.$t('iFrame.minParticipants', this.event.minParticipants)
            }
            if (this.event.minParticipants && this.event.maxParticipants) {
                string += " " + this.$t('iFrame.and') + " "
            }
            if (this.event.maxParticipants) {
                string += this.$t('iFrame.maxParticipants', this.event.maxParticipants)
            }

            if (this.event.minParticipants || this.event.maxParticipants) {
                string += " " + this.$t('iFrame.participants') + "."
            }

            if (this.event.minParticipantsMustBeReachedUntil) {
                string += " " + this.$t('iFrame.minParticipantsMustBeReachedUntil', {date: this.$df(this.$moment(this.event.minParticipantsMustBeReachedUntil), 'dateLong')}) + "."
            }
            return upperFirst(string)
        },
        isDocumentDetailsElementVisible() {
            if (Array.isArray(this.event.documents) && this.event.documents.length) return true
            if (this.privacyDocument) return true
            if (this.termsDocument) return true
            if (this.event.infosheetDocument) return true

            return false
        },
        privacyDocument() {
            return this.getCurrentDocument(this.event.privacyDocuments)
        },
        termsDocument() {
            return this.getCurrentDocument(this.event.termsDocuments)
        },
        infosheetDocument() {
            return this.getCurrentDocument(this.event.infosheetDocument)
        }
    },
    methods: {
        getCurrentDocument(documents) {
            if (!Array.isArray(documents)) return null
            return documents.find((document) => {
                return (document.validFrom === null || this.$moment(document.validFrom).isSameOrBefore(this.$moment())) &&
                    (document.validUntil === null || this.$moment().isSameOrBefore(document.validUntil))
            })
        }
    }
}
</script>

<style lang="scss" scoped>
</style>
